import {
  LayoutDashboard, Workflow, Users, Film, Image,
  BookOpen, Settings, Zap, ChevronRight, Circle, LogOut
} from 'lucide-react'
import useStore from '../../store/useStore'

const NAV_ITEMS = [
  { id: 'dashboard',           label: 'Dashboard',         icon: LayoutDashboard },
  { id: 'quick-image',         label: 'Image Generator',   icon: Image },
  { id: 'quick-video',         label: 'Video Generator',   icon: Film },
  { id: 'canvas',              label: 'Canvas',            icon: Workflow },
  { id: 'character-manager',   label: 'Characters',        icon: Users },
  { id: 'storyboard',          label: 'Storyboard',        icon: BookOpen },
  { id: 'clip-library',        label: 'Clip Library',      icon: Film },
  { id: 'image-library',       label: 'Image Library',     icon: Image },
  { id: 'settings',            label: 'Settings',          icon: Settings },
]

export default function Sidebar() {
  const { currentPage, setCurrentPage, activeJobs, stats } = useStore()
  const hasActiveJobs = activeJobs.length > 0

  return (
    <aside className="w-64 flex-shrink-0 bg-surface-card border-r border-surface-border flex flex-col h-full">

      {/* Logo */}
      <div className="px-5 py-5 border-b border-surface-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-brand flex items-center justify-center shadow-glow-brand">
            <Zap size={18} className="text-white" />
          </div>
          <div>
            <div className="text-white font-bold text-base leading-tight tracking-tight">AIQ Studio</div>
            <div className="text-slate-500 text-xs">Business IQ Edge</div>
          </div>
        </div>
      </div>

      {/* Active jobs indicator */}
      {hasActiveJobs && (
        <div className="mx-3 mt-3 px-3 py-2 rounded-lg bg-brand-500/10 border border-brand-500/20">
          <div className="flex items-center gap-2">
            <Circle size={8} className="text-green-400 fill-green-400 animate-pulse" />
            <span className="text-xs text-brand-300 font-medium">
              {activeJobs.length} job{activeJobs.length > 1 ? 's' : ''} running
            </span>
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map(({ id, label, icon: Icon }) => {
          const active = currentPage === id
          return (
            <button
              key={id}
              onClick={() => setCurrentPage(id)}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                transition-all duration-150 group
                ${active
                  ? 'bg-brand-500/15 text-brand-300 border border-brand-500/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-surface-hover'
                }
              `}
            >
              <Icon size={17} className={active ? 'text-brand-400' : 'text-slate-500 group-hover:text-slate-300'} />
              <span className="flex-1 text-left">{label}</span>
              {active && <ChevronRight size={14} className="text-brand-400" />}
            </button>
          )
        })}
      </nav>

      {/* Bottom stats */}
      <div className="p-4 border-t border-surface-border">
        <div className="grid grid-cols-2 gap-2">
          <StatPill label="Images" value={stats.totalImages} />
          <StatPill label="Clips" value={stats.totalClips} />
          <StatPill label="Videos" value={stats.totalVideos} />
          <StatPill label="Spend" value={`$${stats.totalSpend.toFixed(2)}`} />
        </div>

        {/* Logout */}
        <a
          href="/cdn-cgi/access/logout"
          className="mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-lg
            text-slate-500 hover:text-red-400 hover:bg-red-500/10
            transition-all duration-150 text-xs font-medium"
        >
          <LogOut size={13} />
          Log out
        </a>

        <div className="mt-1 text-center">
          <span className="text-xs text-slate-600">AIQ Studio v1.0</span>
        </div>
      </div>

    </aside>
  )
}

function StatPill({ label, value }) {
  return (
    <div className="bg-surface rounded-lg p-2 text-center border border-surface-border">
      <div className="text-sm font-semibold text-slate-200">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  )
}
